// ChannelWriteService.js

// Firebase
import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocFromServer,
  getDocs,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getDownloadURL, ref, uploadBytes } from 'firebase/storage';
import { auth, collections, db, storage } from 'firebaseConfig';

// Services
import { assertBaseUploadAllowed } from 'services/bases/BaseUploadPolicyService';
import { notifyBaseLimitError } from 'utilities/baseLimitFeedback';

// Utilities
import { CHANNEL_TYPES, isAiConversation, isThreadableChannel, normalizeChannelType } from 'channels/constants/channelTypes';
import { MESSAGE_TYPES } from 'spaces/constants/aiConstants';
import {
  buildConversationChannelKey,
  buildConversationMemberKeys,
  buildConversationMemberKeysHash,
} from 'channels/utils/conversationMemberKeys';
import { withBaseKey } from 'utilities/basePersistence';
import { buildBaseStoragePath } from 'utilities/baseStoragePaths';
import { generateKey } from 'utilities/Keys';

const BASES_COLLECTION = collections.bases;

/**
 * Ceiling on per-channel thread read markers. Older entries are pruned so a busy
 * channel cannot grow the receipt doc without bound.
 */
export const MAX_THREAD_READ_ENTRIES = 500;

/** Hard ceiling on channel memberKeys. Additions past this are rejected. */
export const MAX_CHANNEL_MEMBER_KEYS = 500;

const threadReadMillis = (value) => {
  if (typeof value?.toMillis === 'function') {
    try {
      return value.toMillis();
    } catch (_error) {
      return 0;
    }
  }
  if (value instanceof Date) return value.getTime();
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * Keeps the newest N thread-read markers.
 *
 * @param {object} threadReads - Map of rootKey → timestamp.
 * @param {number} [maxEntries]
 * @returns {object} Pruned map.
 */
export function pruneThreadReads(threadReads, maxEntries = MAX_THREAD_READ_ENTRIES) {
  const entries = Object.entries(threadReads && typeof threadReads === 'object' ? threadReads : {});
  if (entries.length <= maxEntries) {
    return Object.fromEntries(entries);
  }

  entries.sort((left, right) => threadReadMillis(left[1]) - threadReadMillis(right[1]));
  return Object.fromEntries(entries.slice(entries.length - maxEntries));
}

/**
 * Dedupes and validates a channel memberKeys array against the hard ceiling.
 *
 * Never silently truncates — dropping members would revoke access. Callers must
 * reject oversized additions instead.
 *
 * @param {Array<string>|null|undefined} memberKeys
 * @param {object} [options]
 * @param {boolean} [options.allowOversized] - Permit reading legacy oversized sets.
 * @returns {string[]}
 */
export function normalizeChannelMemberKeys(memberKeys, { allowOversized = false } = {}) {
  const cleanMemberKeys = Array.from(new Set(
    (Array.isArray(memberKeys) ? memberKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));

  if (!allowOversized && cleanMemberKeys.length > MAX_CHANNEL_MEMBER_KEYS) {
    const error = new Error(`Channels are limited to ${MAX_CHANNEL_MEMBER_KEYS} members.`);
    error.code = 'failed-precondition';
    throw error;
  }

  return cleanMemberKeys;
}

const channelDocRef = (baseKey, channelKey) => (
  doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey)
);

const channelMessageDocRef = (baseKey, channelKey, messageKey) => (
  doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'messages', messageKey)
);

const channelReadDocRef = (baseKey, channelKey, userKey) => (
  doc(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'reads', userKey)
);

function supportsThreadReplies(channelData) {
  if (isAiConversation(channelData)) return true;
  return isThreadableChannel(channelData?.channelType);
}

function isAiThreadRootMessage(rootData) {
  const messageType = String(rootData?.messageType || '').trim();
  // Any top-level Ai message may be a thread root (chat, feed, or surface).
  return !messageType
    || messageType === MESSAGE_TYPES.AI_FEED_ITEM
    || messageType === MESSAGE_TYPES.AI_SURFACE
    || messageType === MESSAGE_TYPES.AI_TEXT
    || messageType === MESSAGE_TYPES.USER_TEXT;
}

const toCleanText = (value) => String(value || '').trim();

const truncate = (value, max = 220) => {
  const text = String(value || '').trim();
  if (!text) return '';
  if (text.length <= max) return text;
  return `${text.slice(0, max - 1)}...`;
};

const sanitizeFileName = (value = '') => (
  String(value || 'image')
    .replace(/[^a-zA-Z0-9._-]/g, '_')
    .replace(/_+/g, '_')
    .slice(0, 80)
);

const resolveAuthUserKey = (userKey) => {
  const authUid = String(auth.currentUser?.uid || '').trim();
  const providedUserKey = String(userKey || '').trim();

  if (!authUid) {
    return null;
  }

  if (providedUserKey && providedUserKey !== authUid) {
    console.warn('[ChannelWriteService] userKey does not match auth uid; using auth uid for write.', {
      providedUserKey,
      authUid,
    });
  }

  return authUid;
};

const assertSignedInForChannelWrite = (userKey) => {
  const authUid = resolveAuthUserKey(userKey);

  if (!authUid) {
    const error = new Error('You must be signed in to send messages.');
    error.code = 'auth/missing';
    throw error;
  }

  return authUid;
};

/**
 * Creates a base channel.
 *
 * @param {object} options - Channel options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.userKey - User key.
 * @param {string} options.userName - User display name.
 * @param {string} options.title - Channel title.
 * @param {string} options.topic - Optional topic.
 * @param {string} options.channelType - Channel type key.
 *
 * @returns {Promise<object|null>} Created channel payload.
 */
export async function createChannel({
  baseKey,
  userKey,
  userName,
  title,
  topic,
  channelType,
} = {}) {
  const cleanTitle = toCleanText(title);
  const cleanTopic = toCleanText(topic);
  const cleanChannelType = normalizeChannelType(channelType);

  if (!baseKey || !userKey || !cleanTitle) return null;

  const channelKey = generateKey();
  const now = Timestamp.now();
  const creatorKey = String(userKey || '').trim();
  const memberKeys = normalizeChannelMemberKeys(creatorKey ? [creatorKey] : []);

  const payload = withBaseKey({
    key: channelKey,
    title: cleanTitle,
    titleLower: cleanTitle.toLowerCase(),
    topic: cleanTopic,
    channelType: cleanChannelType,
    memberKeys,
    memberCount: memberKeys.length,
    createdBy: creatorKey,
    createdByName: userName || 'Member',
    dateCreated: now,
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  }, baseKey);

  await setDoc(channelDocRef(baseKey, channelKey), payload, { merge: false });
  return payload;
}

const buildReplyPreviewText = (text, attachments = []) => {
  const cleanText = String(text || '').trim();
  if (cleanText) return cleanText;
  if (Array.isArray(attachments) && attachments.length > 0) return 'Shared';
  return 'Sent a message';
};

const channelMessagesCollectionRef = (baseKey, channelKey) => (
  collection(db, BASES_COLLECTION, baseKey, 'channels', channelKey, 'messages')
);

/**
 * Recomputes denormalized thread summary fields on a root message.
 *
 * @param {object} options - Recompute options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.threadRootKey - Thread root message key.
 * @returns {Promise<void>}
 */
async function recomputeThreadRootSummary({ baseKey, channelKey, threadRootKey } = {}) {
  const cleanRootKey = String(threadRootKey || '').trim();
  if (!baseKey || !channelKey || !cleanRootKey) return;

  const repliesQuery = query(
    channelMessagesCollectionRef(baseKey, channelKey),
    where('threadRootKey', '==', cleanRootKey),
    orderBy('dateCreated', 'asc')
  );

  const snapshot = await getDocs(repliesQuery);
  const replies = snapshot.docs
    .map((docSnap) => docSnap.data())
    .filter((row) => row && !row.dateDeleted);

  const rootRef = channelMessageDocRef(baseKey, channelKey, cleanRootKey);

  if (replies.length === 0) {
    await updateDoc(rootRef, {
      replyCount: 0,
      lastReplyAt: null,
      lastReplyPreview: '',
      lastReplyByKey: null,
      lastReplyByName: '',
      dateModified: Timestamp.now(),
    });
    return;
  }

  const lastReply = replies[replies.length - 1];
  const previewText = buildReplyPreviewText(lastReply?.text, lastReply?.attachments);

  await updateDoc(rootRef, {
    replyCount: replies.length,
    lastReplyAt: lastReply?.dateCreated || Timestamp.now(),
    lastReplyPreview: truncate(previewText),
    lastReplyByKey: lastReply?.userKey || null,
    lastReplyByName: lastReply?.userName || 'Member',
    dateModified: Timestamp.now(),
  });
}

/**
 * Finds or creates a base conversation for a participant set.
 *
 * @param {object} options - Conversation options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.userKey - Current user key.
 * @param {string} options.userName - Current user display name.
 * @param {Array<string|object>} options.selectedMemberKeys - Selected member keys/people.
 * @returns {Promise<{ channel: object|null, created: boolean }>} Conversation result.
 */
export async function getOrCreateConversation({
  baseKey,
  userKey,
  userName,
  selectedMemberKeys = [],
} = {}) {
  const cleanBaseKey = String(baseKey || '').trim();
  const cleanUserKey = String(userKey || '').trim();

  if (!cleanBaseKey || !cleanUserKey) {
    return { channel: null, created: false };
  }

  const memberKeys = normalizeChannelMemberKeys(buildConversationMemberKeys({
    currentUserKey: cleanUserKey,
    selectedMemberKeys,
  }));

  if (memberKeys.length === 0) {
    return { channel: null, created: false };
  }

  const dmMemberKeysHash = buildConversationMemberKeysHash(memberKeys, cleanUserKey);
  const channelKey = buildConversationChannelKey(dmMemberKeysHash);
  const ref = channelDocRef(cleanBaseKey, channelKey);

  const existingSnap = await getDoc(ref);
  if (existingSnap.exists()) {
    return {
      channel: {
        id: existingSnap.id,
        ...(existingSnap.data() || {}),
        key: existingSnap.id,
      },
      created: false,
    };
  }

  const now = Timestamp.now();

  const payload = withBaseKey({
    key: channelKey,
    channelType: CHANNEL_TYPES.CONVERSATION,
    dmMemberKeysHash,
    memberKeys,
    memberCount: memberKeys.length,
    title: 'Conversation',
    titleLower: 'conversation',
    topic: '',
    createdBy: cleanUserKey,
    createdByName: userName || 'Member',
    messagingEnabled: true,
    messagingDisabledReason: null,
    inactiveMemberKeys: [],
    dateCreated: now,
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  }, cleanBaseKey);

  try {
    await setDoc(ref, payload, { merge: false });
    return { channel: payload, created: true };
  } catch (error) {
    const existingSnap = await getDoc(ref);
    if (existingSnap.exists()) {
      return {
        channel: {
          id: existingSnap.id,
          ...(existingSnap.data() || {}),
          key: existingSnap.id,
        },
        created: false,
      };
    }

    throw error;
  }
}

/**
 * Persists messaging-disabled state for a conversation.
 *
 * @param {object} options - Update options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string[]} options.inactiveMemberKeys - Member keys no longer in base.
 * @param {string} options.reason - Disabled reason code.
 * @returns {Promise<void>}
 */
export async function updateConversationMessagingState({
  baseKey,
  channelKey,
  inactiveMemberKeys = [],
  reason = 'MEMBER_LEFT_BASE',
} = {}) {
  if (!baseKey || !channelKey) return;

  await updateDoc(channelDocRef(baseKey, channelKey), {
    messagingEnabled: false,
    messagingDisabledReason: reason,
    inactiveMemberKeys: Array.from(new Set(
      (Array.isArray(inactiveMemberKeys) ? inactiveMemberKeys : [])
        .map((key) => String(key || '').trim())
        .filter(Boolean)
    )),
    dateModified: Timestamp.now(),
  });
}

/**
 * Marks a channel as read for a user.
 *
 * @param {object} options - Read receipt options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.userKey - User key.
 * @param {Timestamp|Date|string} options.lastReadAt - Last read marker.
 *
 * @returns {Promise<void>}
 */
export async function markChannelRead({
  baseKey,
  channelKey,
  userKey,
  lastReadAt,
  lastReadMessageKey,
} = {}) {
  if (!baseKey || !channelKey || !userKey) return;

  const payload = withBaseKey({
    key: userKey,
    userKey,
    channelKey,
    lastReadAt: lastReadAt || serverTimestamp(),
    dateModified: serverTimestamp(),
  }, baseKey);

  const cleanMessageKey = String(lastReadMessageKey || '').trim();
  if (cleanMessageKey) {
    payload.lastReadMessageKey = cleanMessageKey;
  }

  await setDoc(
    channelReadDocRef(baseKey, channelKey, userKey),
    payload,
    { merge: true }
  );
}

/**
 * Marks a thread as read for a user.
 *
 * @param {object} options - Thread read options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.userKey - User key.
 * @param {string} options.threadRootKey - Thread root message key.
 * @param {Timestamp|Date|string} options.lastReadAt - Last read marker.
 * @returns {Promise<void>}
 */
export async function markThreadRead({
  baseKey,
  channelKey,
  userKey,
  threadRootKey,
  lastReadAt,
} = {}) {
  const cleanRootKey = String(threadRootKey || '').trim();
  if (!baseKey || !channelKey || !userKey || !cleanRootKey) return;

  const readRef = channelReadDocRef(baseKey, channelKey, userKey);
  // Concrete timestamp so pruning can compare entries; serverTimestamp() cannot be
  // read back inside the same transaction.
  const resolvedLastReadAt = lastReadAt || Timestamp.now();

  await runTransaction(db, async (transaction) => {
    const existingSnap = await transaction.get(readRef);
    const existingData = existingSnap.exists() ? (existingSnap.data() || {}) : {};
    const nextThreadReads = pruneThreadReads({
      ...(existingData.threadReads || {}),
      [cleanRootKey]: resolvedLastReadAt,
    });

    transaction.set(readRef, withBaseKey({
      key: userKey,
      userKey,
      channelKey,
      threadReads: nextThreadReads,
      dateModified: serverTimestamp(),
    }, baseKey), { merge: true });
  });
}

/**
 * Uploads image attachments for a channel message.
 *
 * @param {object} options - Upload options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {File[]} options.files - Files to upload.
 *
 * @returns {Promise<object[]>} Uploaded image attachment rows.
 */
export async function uploadChannelImageAttachments({ baseKey, channelKey, messageKey, files = [] } = {}) {
  if (!baseKey || !channelKey || !messageKey) return [];
  if (!Array.isArray(files) || files.length === 0) return [];

  try {
    const uploads = files.map(async (file, index) => {
      if (!file) return null;

      await assertBaseUploadAllowed({
        baseKey,
        fileName: file.name,
        fileSizeBytes: file.size,
      });

      const fileName = sanitizeFileName(file.name || `image-${index + 1}.jpg`);
      const path = buildBaseStoragePath(
        baseKey,
        `channels/${channelKey}/messages/${messageKey}/${index + 1}_${fileName}`,
      );
      const objectRef = ref(storage, path);

      await uploadBytes(objectRef, file, {
        contentType: file.type || 'image/jpeg',
        cacheControl: 'public,max-age=3600',
      });

      const downloadURL = await getDownloadURL(objectRef);

      return {
        key: generateKey(),
        type: 'image',
        name: file.name || fileName,
        mimeType: file.type || 'image/jpeg',
        path,
        size: Number(file.size) || 0,
        url: downloadURL,
      };
    });

    const results = await Promise.all(uploads);
    return results.filter(Boolean);
  } catch (error) {
    notifyBaseLimitError({ error, baseKey });
    throw error;
  }
}

/**
 * Creates a message in a channel and updates channel metadata.
 *
 * @param {object} options - Message options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.userKey - User key.
 * @param {object} options.user - Optional user summary snapshot.
 * @param {string} options.userName - User display name.
 * @param {string} options.userPhoto - Optional user photo URL.
 * @param {string} options.text - Message body.
 * @param {object[]} options.attachments - Message attachments.
 * @param {string} options.messageKey - Optional pre-generated message key.
 *
 * @returns {Promise<object|null>} Created message payload.
 */
export async function postChannelMessage({
  baseKey,
  channelKey,
  userKey,
  user,
  userName,
  userPhoto,
  text,
  attachments = [],
  mentionKeys = [],
  messageKey,
  messageType,
} = {}) {
  const cleanText = String(text || '').trim();
  const cleanAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  const cleanMentionKeys = Array.from(new Set(
    (Array.isArray(mentionKeys) ? mentionKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));

  if (!baseKey || !channelKey || !userKey) return null;
  if (!cleanText && cleanAttachments.length === 0) return null;

  const authUid = assertSignedInForChannelWrite(userKey);

  if (auth.currentUser?.getIdToken) {
    await auth.currentUser.getIdToken();
  }

  const nextMessageKey = messageKey || generateKey();
  const now = Timestamp.now();
  const userSummary = user || null;
  const safeUserName = userSummary?.name || userName || 'Member';
  const safeUserPhoto = userSummary?.coverUrl || userPhoto || '';

  const payload = withBaseKey({
    key: nextMessageKey,
    channelKey,
    userKey: authUid,
    user: userSummary,
    userName: safeUserName,
    userPhoto: safeUserPhoto,
    text: cleanText,
    attachments: cleanAttachments,
    isEdited: false,
    dateCreated: now,
    dateModified: now,
    dateDeleted: null,
    parentMessageKey: null,
    threadRootKey: null,
    mentionKeys: cleanMentionKeys,
  }, baseKey);

  const cleanMessageType = String(messageType || '').trim();
  if (cleanMessageType) {
    payload.messageType = cleanMessageType;
  }

  const previewText = cleanText || (cleanAttachments.length > 0 ? 'Shared' : 'Sent a message');
  const messageRef = channelMessageDocRef(baseKey, channelKey, nextMessageKey);

  try {
    await setDoc(messageRef, payload, { merge: false });
  } catch (error) {
    console.error('[ChannelWriteService] postChannelMessage create failed:', {
      code: error?.code,
      message: error?.message,
      baseKey,
      channelKey,
      messageKey: nextMessageKey,
      userKey: authUid,
      authUid,
    });
    throw error;
  }

  try {
    await updateDoc(channelDocRef(baseKey, channelKey), {
      dateModified: now,
      lastMessageAt: now,
      lastMessageByKey: authUid,
      lastMessageByName: safeUserName,
      lastMessagePreview: truncate(previewText),
    });
  } catch (error) {
    console.warn('[ChannelWriteService] postChannelMessage channel activity failed:', {
      code: error?.code,
      message: error?.message,
      baseKey,
      channelKey,
      messageKey: nextMessageKey,
      userKey: authUid,
      authUid,
    });
  }

  return payload;
}

/**
 * Creates a thread reply and updates root summary metadata.
 *
 * @param {object} options - Thread reply options.
 * @returns {Promise<object|null>} Created reply payload.
 */
export async function postChannelThreadReply({
  baseKey,
  channelKey,
  threadRootKey,
  userKey,
  user,
  userName,
  userPhoto,
  text,
  attachments = [],
  mentionKeys = [],
  messageKey,
} = {}) {
  const cleanRootKey = String(threadRootKey || '').trim();
  const cleanText = String(text || '').trim();
  const cleanAttachments = Array.isArray(attachments) ? attachments.filter(Boolean) : [];
  const cleanMentionKeys = Array.from(new Set(
    (Array.isArray(mentionKeys) ? mentionKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));

  if (!baseKey || !channelKey || !cleanRootKey || !userKey) return null;
  if (!cleanText && cleanAttachments.length === 0) return null;

  const authUserKey = assertSignedInForChannelWrite(userKey);

  if (auth.currentUser?.getIdToken) {
    await auth.currentUser.getIdToken();
  }

  let channelSnap;
  let rootSnap;

  try {
    [channelSnap, rootSnap] = await Promise.all([
      getDocFromServer(channelDocRef(baseKey, channelKey)),
      getDocFromServer(channelMessageDocRef(baseKey, channelKey, cleanRootKey)),
    ]);
  } catch (error) {
    console.error('[ChannelWriteService] postChannelThreadReply preflight read failed:', {
      code: error?.code,
      message: error?.message,
      baseKey,
      channelKey,
      threadRootKey: cleanRootKey,
      authUid: authUserKey,
      providedUserKey: String(userKey || '').trim(),
    });
    throw error;
  }

  const channelData = channelSnap.exists() ? (channelSnap.data() || {}) : null;

  if (!channelSnap.exists() || !supportsThreadReplies(channelData)) {
    return null;
  }

  if (channelData?.messagingEnabled === false) {
    const error = new Error('Messaging is disabled in this channel.');
    error.code = 'channel/messaging-disabled';
    throw error;
  }

  if (!rootSnap.exists()) return null;

  const rootData = rootSnap.data() || {};
  if (rootData.parentMessageKey || rootData.threadRootKey) {
    return null;
  }

  if (isAiConversation(channelData) && !isAiThreadRootMessage(rootData)) {
    return null;
  }

  const nextMessageKey = messageKey || generateKey();
  const now = Timestamp.now();
  const userSummary = user || null;
  const safeUserName = userSummary?.name || userName || 'Member';
  const safeUserPhoto = userSummary?.coverUrl || userPhoto || '';
  const previewText = buildReplyPreviewText(cleanText, cleanAttachments);
  const currentReplyCount = Number(rootData.replyCount) || 0;
  const nextParticipantKeys = Array.from(new Set(
    [
      ...(Array.isArray(rootData.threadParticipantKeys) ? rootData.threadParticipantKeys : []),
      authUserKey,
    ]
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));

  const payload = withBaseKey({
    key: nextMessageKey,
    channelKey,
    userKey: authUserKey,
    user: userSummary,
    userName: safeUserName,
    userPhoto: safeUserPhoto,
    text: cleanText,
    attachments: cleanAttachments,
    isEdited: false,
    dateCreated: now,
    dateModified: now,
    dateDeleted: null,
    parentMessageKey: cleanRootKey,
    threadRootKey: cleanRootKey,
    mentionKeys: cleanMentionKeys,
  }, baseKey);

  const rootUpdate = {
    replyCount: currentReplyCount + 1,
    lastReplyAt: now,
    lastReplyPreview: truncate(previewText),
    lastReplyByKey: authUserKey,
    lastReplyByName: safeUserName,
    threadParticipantKeys: nextParticipantKeys,
    dateModified: now,
  };

  if (!String(rootData?.key || '').trim()) {
    rootUpdate.key = cleanRootKey;
  }

  if (!String(rootData?.baseKey || '').trim()) {
    Object.assign(rootUpdate, withBaseKey({}, baseKey));
  }

  if (!String(rootData?.channelKey || '').trim()) {
    rootUpdate.channelKey = channelKey;
  }

  const replyRef = channelMessageDocRef(baseKey, channelKey, nextMessageKey);
  const rootRef = channelMessageDocRef(baseKey, channelKey, cleanRootKey);

  try {
    await setDoc(replyRef, payload, { merge: false });
  } catch (error) {
    console.error('[ChannelWriteService] postChannelThreadReply create failed:', {
      code: error?.code,
      message: error?.message,
      baseKey,
      channelKey,
      threadRootKey: cleanRootKey,
      messageKey: nextMessageKey,
      authUid: authUserKey,
      providedUserKey: String(userKey || '').trim(),
      channelType: channelData?.channelType || null,
      messagingEnabled: channelData?.messagingEnabled ?? null,
      memberKeys: Array.isArray(channelData?.memberKeys) ? channelData.memberKeys : null,
      parentMessageKey: payload.parentMessageKey,
      threadRootKeyOnPayload: payload.threadRootKey,
    });
    throw error;
  }

  try {
    await updateDoc(rootRef, rootUpdate);
  } catch (error) {
    console.warn('[ChannelWriteService] postChannelThreadReply root update failed:', {
      code: error?.code,
      message: error?.message,
      baseKey,
      channelKey,
      threadRootKey: cleanRootKey,
      messageKey: nextMessageKey,
      rootUpdate,
    });
  }

  return payload;
}

/**
 * Edits an existing channel message.
 *
 * @param {object} options - Edit options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {string} options.text - Updated message text.
 *
 * @returns {Promise<void>}
 */
export async function editChannelMessage({ baseKey, channelKey, messageKey, text } = {}) {
  const cleanText = toCleanText(text);
  if (!baseKey || !channelKey || !messageKey || !cleanText) return;

  const messageRef = channelMessageDocRef(baseKey, channelKey, messageKey);
  const messageSnap = await getDoc(messageRef);
  if (!messageSnap.exists()) return;

  const messageData = messageSnap.data() || {};
  const now = Timestamp.now();

  await updateDoc(messageRef, {
    text: cleanText,
    isEdited: true,
    dateModified: now,
  });

  const threadRootKey = String(messageData?.threadRootKey || '').trim();
  if (threadRootKey) {
    const rootSnap = await getDoc(channelMessageDocRef(baseKey, channelKey, threadRootKey));
    const rootData = rootSnap.exists() ? (rootSnap.data() || {}) : {};
    if (rootData.lastReplyByKey === messageData.userKey) {
      await updateDoc(channelMessageDocRef(baseKey, channelKey, threadRootKey), {
        lastReplyPreview: truncate(cleanText),
        dateModified: now,
      });
    }
    return;
  }

  await updateDoc(channelDocRef(baseKey, channelKey), {
    dateModified: now,
    lastMessagePreview: truncate(cleanText),
  });
}

/**
 * Soft deletes an existing channel message.
 *
 * @param {object} options - Delete options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.messageKey - Message key.
 * @param {string} options.userKey - User key.
 *
 * @returns {Promise<void>}
 */
export async function deleteChannelMessage({ baseKey, channelKey, messageKey, userKey } = {}) {
  if (!baseKey || !channelKey || !messageKey) return;

  const messageRef = channelMessageDocRef(baseKey, channelKey, messageKey);
  const messageSnap = await getDoc(messageRef);
  if (!messageSnap.exists()) return;

  const messageData = messageSnap.data() || {};
  const now = Timestamp.now();

  await updateDoc(messageRef, {
    text: '',
    attachments: [],
    dateDeleted: now,
    deletedBy: userKey || null,
    dateModified: now,
  });

  const threadRootKey = String(messageData?.threadRootKey || '').trim();
  if (threadRootKey) {
    await recomputeThreadRootSummary({
      baseKey,
      channelKey,
      threadRootKey,
    });
    return;
  }

  await updateDoc(channelDocRef(baseKey, channelKey), {
    dateModified: now,
    lastMessagePreview: 'Message deleted',
  });
}

const CLEAR_MESSAGES_BATCH_SIZE = 450;

/**
 * Hard-deletes all messages in a channel and resets channel summary fields.
 *
 * @param {object} options - Clear options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 *
 * @returns {Promise<number>} Number of deleted messages.
 */
export async function clearChannelMessages({ baseKey, channelKey } = {}) {
  if (!baseKey || !channelKey) return 0;

  const messagesRef = collection(
    db,
    BASES_COLLECTION,
    baseKey,
    'channels',
    channelKey,
    'messages',
  );

  const snapshot = await getDocs(query(messagesRef, orderBy('dateCreated', 'asc')));
  const messageDocs = snapshot.docs;
  if (!messageDocs.length) return 0;

  const now = Timestamp.now();

  for (let offset = 0; offset < messageDocs.length; offset += CLEAR_MESSAGES_BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = messageDocs.slice(offset, offset + CLEAR_MESSAGES_BATCH_SIZE);
    chunk.forEach((messageDoc) => {
      batch.delete(messageDoc.ref);
    });
    await batch.commit();
  }

  await updateDoc(channelDocRef(baseKey, channelKey), {
    dateModified: now,
    lastMessageAt: null,
    lastMessageByKey: null,
    lastMessageByName: null,
    lastMessagePreview: '',
  });

  return messageDocs.length;
}

/**
 * Updates channel properties.
 *
 * @param {object} options - Update options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string} options.title - Channel title.
 * @param {string} options.topic - Channel description/topic.
 *
 * @returns {Promise<void>}
 */
export async function updateChannel({ baseKey, channelKey, title, topic } = {}) {
  const cleanTitle = toCleanText(title);
  const cleanTopic = toCleanText(topic);

  if (!baseKey || !channelKey || !cleanTitle) return;

  await updateDoc(channelDocRef(baseKey, channelKey), {
    title: cleanTitle,
    titleLower: cleanTitle.toLowerCase(),
    topic: cleanTopic,
    dateModified: Timestamp.now(),
  });
}

/**
 * Updates channel membership keys.
 *
 * @param {object} options - Update options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 * @param {string[]} options.memberKeys - Next channel member keys.
 * @param {boolean} options.privileged - Whether caller can remove others (creator/admin/owner).
 *
 * @returns {Promise<void>}
 */
export async function updateChannelMembers({
  baseKey,
  channelKey,
  memberKeys = [],
  privileged = false,
} = {}) {
  if (!baseKey || !channelKey) return;

  const channelRef = channelDocRef(baseKey, channelKey);
  const channelSnap = await getDocFromServer(channelRef);
  const channelData = channelSnap.exists() ? channelSnap.data() : null;

  const currentMemberKeys = normalizeChannelMemberKeys(channelData?.memberKeys, {
    allowOversized: true,
  });

  // Removals from a legacy oversized channel are allowed; only growth past the
  // ceiling is rejected.
  const cleanMemberKeys = normalizeChannelMemberKeys(memberKeys, {
    allowOversized: cleanMemberKeysWouldShrink(currentMemberKeys, memberKeys),
  });

  if (!privileged) {
    const authUid = String(auth.currentUser?.uid || '').trim();
    const removedKeys = currentMemberKeys.filter((key) => !cleanMemberKeys.includes(key));
    const addedKeys = cleanMemberKeys.filter((key) => !currentMemberKeys.includes(key));
    const isSuperset = removedKeys.length === 0;
    const isSelfLeaveOnly = removedKeys.length === 1
      && addedKeys.length === 0
      && authUid
      && removedKeys[0] === authUid;

    if (!isSuperset && !isSelfLeaveOnly) {
      const error = new Error('You can only add members or leave the channel.');
      error.code = 'permission-denied';
      throw error;
    }
  }

  await updateDoc(channelRef, {
    memberKeys: cleanMemberKeys,
    memberCount: cleanMemberKeys.length,
    dateModified: Timestamp.now(),
  });
}

function cleanMemberKeysWouldShrink(currentMemberKeys, nextMemberKeys) {
  const next = Array.from(new Set(
    (Array.isArray(nextMemberKeys) ? nextMemberKeys : [])
      .map((key) => String(key || '').trim())
      .filter(Boolean)
  ));
  return next.length <= currentMemberKeys.length;
}

/**
 * Deletes a channel document.
 *
 * @param {object} options - Delete options.
 * @param {string} options.baseKey - Base key.
 * @param {string} options.channelKey - Channel key.
 *
 * @returns {Promise<void>}
 */
export async function deleteChannel({ baseKey, channelKey } = {}) {
  if (!baseKey || !channelKey) return;
  await deleteDoc(channelDocRef(baseKey, channelKey));
}
